import React from "react";
import { cn } from "../../utils/cn";

const CommonSkeletonLoader = ({
  type = "table",
  rows = 9,
  tableLength = 7,
}) => {
  if (!type) return null;

  if (type === "profile") {
    const skeletonClass = "bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse";
    return (
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm flex items-center justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className={`w-24 h-24 rounded-full border-4 border-white shadow-sm ${skeletonClass}`} />
            <div className="space-y-3">
              <div className={`h-8 w-48 rounded ${skeletonClass}`} />
              <div className="space-y-2">
                <div className={`h-4 w-24 rounded ${skeletonClass}`} />
                <div className={`h-4 w-40 rounded ${skeletonClass}`} />
              </div>
            </div>
          </div>
          <div className="space-y-2 flex flex-col items-end">
            <div className={`h-10 w-32 rounded-lg ${skeletonClass}`} />
            <div className={`h-10 w-40 rounded-lg ${skeletonClass}`} />
          </div>
        </div>

        {/* Personal Information Card */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-8 py-4 border-b border-gray-50 bg-gray-50/30">
            <div className={`h-6 w-40 rounded ${skeletonClass}`} />
          </div>
          <div className="p-6 px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-12">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className={`h-3 w-20 rounded ${skeletonClass}`} />
                  <div className={`h-5 w-32 rounded ${skeletonClass}`} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Subscription & Plan Card */}
        <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
          <div className="px-8 py-4 border-b border-gray-50 bg-gray-50/30">
            <div className={`h-6 w-40 rounded ${skeletonClass}`} />
          </div>
          <div className="p-6 px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-12">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="space-y-2">
                  <div className={`h-3 w-20 rounded ${skeletonClass}`} />
                  <div className={`h-6 w-24 rounded-full ${skeletonClass}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (type === "card") {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="border rounded-lg p-4 shadow-sm bg-white"
          >
            {/* Header */}
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-1/3 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
                <div className="h-3 w-1/4 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
              </div>
            </div>

            {/* Content */}
            <div className="space-y-2 mb-3">
              <div className="h-3 w-2/3 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
              <div className="h-3 w-1/2 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
            </div>

            <hr className="my-3 border-slate-200" />

            {/* Stats */}
            <div className="space-y-2">
              {["Verified", "Plan", "Trial", "Status"].map((label) => (
                <div key={label} className="flex justify-between">
                  <div className="h-3 w-16 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
                  <div className="h-4 w-20 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
                </div>
              ))}
            </div>

            <hr className="my-3 border-slate-200" />

            {/* Footer buttons */}
            <div className="flex gap-2">
              <div className="flex-1 h-9 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
              <div className="flex-1 h-9 rounded bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }


  if (type === "table") {
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {/* Table Header */}
        <div className="border-b border-slate-200 bg-slate-50 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="h-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-32" />
            <div className="h-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-48" />
            <div className="h-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-24 ml-auto" />
            <div className="h-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-24" />
            <div className="h-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-20" />
          </div>
        </div>

        {/* Table Rows */}
        <div className="divide-y divide-slate-200">
          {Array.from({ length: tableLength }, (_, i) => i + 1).map((item) => (
            <div key={item} className="px-6 py-4 animate-pulse">
              <div className="flex items-center gap-4">
                {/* Checkbox/Icon */}
                <div className="h-5 w-5 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded" />

                {/* Image/Avatar */}
                <div className="h-12 w-12 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded" />

                {/* Content */}
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-2/3" />
                  <div className="h-3 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-1/2" />
                </div>

                {/* Status */}
                <div className="h-6 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded-full w-20" />

                {/* Date */}
                <div className="h-4 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded w-24" />

                {/* Actions */}
                <div className="flex gap-2">
                  <div className="h-8 w-8 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded-lg" />
                  <div className="h-8 w-8 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 bg-[length:200%_100%] animate-pulse rounded-lg" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default TABLE view
  return null;
};

export default CommonSkeletonLoader;
